import React, { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { GetTheAppButton } from './GooglePlayBadge';
import { isInstalledApp } from '../utils/installedApp';
// Lazy: SupportDialog pulls sonner + radix-dialog (~25 KB gz) — only load it
// when the user actually opens the support form.
const SupportDialog = lazy(() => import('./SupportDialog'));

/**
 * SiteFooter — shared footer used across all public-facing pages
 * (Landing, Ambassadors, Press, Status, FAQs, legal pages, etc.).
 *
 * Single source of truth for site links, brand, legal and contact email.
 * Update once, propagate everywhere.
 *
 * Props
 * -----
 *   • includeBlog (bool, default false) — whether to surface the blog link.
 *     Toggle to true once the blog ships.
 */
const SiteFooter = ({ includeBlog = true }) => {
  const year = new Date().getFullYear();
  const [supportOpen, setSupportOpen] = useState(false);
  return (
    <footer
      className="border-t border-stone-200 bg-white"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Brand */}
        <div className="sm:col-span-2 lg:col-span-1">
          <img
            src="/tovias-logo-footer.webp"
            alt="Tovías"
            width="80"
            height="80"
            loading="lazy"
            className="h-10 w-auto mb-3"
            data-testid="footer-logo"
          />
          <p className="text-xs text-stone-500 max-w-xs leading-relaxed">
            La forma más fácil de organizar viajes con familia o amigos.
          </p>
          <p className="mt-2 text-sm font-serif italic text-teal-700" data-testid="footer-tagline">
            La mejor app para los mejores viajes.
          </p>
          {!isInstalledApp() && <div className="mt-4" data-testid="footer-get-app"><GetTheAppButton className="h-10" /></div>}
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.instagram.com/tovias_app"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-stone-400 hover:text-teal-600 transition-colors"
              data-testid="footer-instagram"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14c-.3.76-.5 1.64-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.8.73 1.48 1.38 2.13.65.65 1.33 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84M12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4Zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44Z"/>
              </svg>
            </a>
            <a
              href="mailto:hola@tovias.app"
              aria-label="Email"
              className="text-stone-400 hover:text-teal-600 transition-colors"
              data-testid="footer-email"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="m22 6-10 7L2 6"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Producto */}
        <div>
          <h4 className="text-xs uppercase tracking-wider font-bold text-stone-400 mb-3">Producto</h4>
          <ul className="space-y-2 text-sm text-stone-600">
            <li><Link to="/funciones"   className="hover:text-stone-900 transition-colors" data-testid="footer-funciones">Funciones</Link></li>
            <li><Link to="/planes"      className="hover:text-stone-900 transition-colors" data-testid="footer-planes">Precios</Link></li>
            <li><Link to="/showcase"    className="hover:text-stone-900 transition-colors" data-testid="footer-showcase">Ver ejemplo</Link></li>
            <li><Link to="/faqs"        className="hover:text-stone-900 transition-colors" data-testid="footer-faqs">Preguntas frecuentes</Link></li>
            <li><Link to="/que-es-tovias" className="hover:text-stone-900 transition-colors" data-testid="footer-about">¿Qué es Tovías?</Link></li>
            <li><Link to="/embajadores" className="hover:text-stone-900 transition-colors" data-testid="footer-ambassadors">Embajadores</Link></li>
            <li><Link to="/rutas"       className="hover:text-stone-900 transition-colors" data-testid="footer-rutas">Rutas de la comunidad</Link></li>
            {includeBlog && (
              <li><Link to="/blog" className="hover:text-stone-900 transition-colors" data-testid="footer-blog">Blog</Link></li>
            )}
            <li><Link to="/status"      className="hover:text-stone-900 transition-colors" data-testid="footer-status">Estado del sistema</Link></li>
          </ul>
        </div>

        {/* Cuenta + Empresa */}
        <div>
          <h4 className="text-xs uppercase tracking-wider font-bold text-stone-400 mb-3">Cuenta</h4>
          <ul className="space-y-2 text-sm text-stone-600 mb-6">
            <li><Link to="/register"    className="hover:text-stone-900 transition-colors" data-testid="footer-register">Crear cuenta gratis</Link></li>
            <li><Link to="/email-login" className="hover:text-stone-900 transition-colors" data-testid="footer-login">Iniciar sesión</Link></li>
          </ul>
          <h4 className="text-xs uppercase tracking-wider font-bold text-stone-400 mb-3">Empresa</h4>
          <ul className="space-y-2 text-sm text-stone-600">
            <li><Link to="/asociaciones" className="hover:text-stone-900 transition-colors" data-testid="footer-associations">Para organizadores y asociaciones</Link></li>
            <li><Link to="/establecimientos" className="hover:text-stone-900 transition-colors" data-testid="footer-venues">Para establecimientos</Link></li>
            <li><Link to="/prensa"      className="hover:text-stone-900 transition-colors" data-testid="footer-press">Para prensa</Link></li>
            <li><Link to="/cumplimiento" className="hover:text-stone-900 transition-colors" data-testid="footer-compliance">Cumplimiento y seguridad</Link></li>
            <li><Link to="/comparativa" className="hover:text-stone-900 transition-colors" data-testid="footer-compare">vs otras apps</Link></li>
            <li><Link to="/apps-viaje" className="hover:text-stone-900 transition-colors" data-testid="footer-apps-viaje">Apps útiles por destino</Link></li>
            <li>
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                className="hover:text-stone-900 transition-colors text-left"
                data-testid="footer-contact"
              >
                Contactar a soporte
              </button>
            </li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h4 className="text-xs uppercase tracking-wider font-bold text-stone-400 mb-3">Legal</h4>
          <ul className="space-y-2 text-sm text-stone-600">
            <li><Link to="/aviso-legal"                  className="hover:text-stone-900 transition-colors" data-testid="footer-aviso">Aviso legal</Link></li>
            <li><Link to="/politica-privacidad"          className="hover:text-stone-900 transition-colors" data-testid="footer-privacy">Política de privacidad</Link></li>
            <li><Link to="/politica-cookies"             className="hover:text-stone-900 transition-colors" data-testid="footer-cookies">Política de cookies</Link></li>
            <li><Link to="/condiciones-uso"              className="hover:text-stone-900 transition-colors" data-testid="footer-uso">Condiciones de uso</Link></li>
            <li><Link to="/condiciones-contratacion"     className="hover:text-stone-900 transition-colors" data-testid="footer-contratacion">Condiciones de contratación</Link></li>
            <li><Link to="/terminos-condiciones"         className="hover:text-stone-900 transition-colors" data-testid="footer-terms">Términos y condiciones</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-stone-100 py-5 text-center text-[11px] text-stone-400 px-4 leading-relaxed">
        © {year} <strong className="text-stone-500">TOVIAS L.L.C.</strong> · 1209 Mountain Road Pl NE, Ste N, Albuquerque, NM 87110 · Todos los derechos reservados
        <br />
        <a href="mailto:hola@tovias.app" className="hover:text-stone-600">hola@tovias.app</a>
        <span className="mx-1.5 text-stone-300">·</span>
        <a href="mailto:legal@tovias.app" className="hover:text-stone-600">legal@tovias.app</a>
        <span className="mx-1.5 text-stone-300">·</span>
        <a href="mailto:privacy@tovias.app" className="hover:text-stone-600">privacy@tovias.app</a>
      </div>

      {/* Support contact dialog — opens via "Contactar a soporte" link.
          The dialog itself handles the unauthenticated case gracefully
          (shows a sign-in CTA) so we don't need to gate the link. */}
      {supportOpen && (
        <Suspense fallback={null}>
          <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
        </Suspense>
      )}
    </footer>
  );
};

export default SiteFooter;
